<script setup lang="ts">
import { reactive, watch, onMounted } from 'vue';

import { LpSetup } from '@thehotelsnetwork/lampo';
import { useProductTour } from '@thehotelsnetwork/vue-product-tour';
import { useChatBot, useSatismeter } from '@thehotelsnetwork/usables';
import { RouterView, useRouter } from 'vue-router';

import useMainState from '@/state/useMainState';
import useUserState from '@/state/useUserState';

import auth from '@/modules/Auth';
import { getTechTracking } from '@/modules/techTracking';
import { getUserTypeDefaultRedirect } from '@/utils/getUserTypeDefaultRedirect';

import browserStorage from '@/modules/browserStorage';
import getUserIntercomCredentials from '@/utils/getUserIntercomCredentials';

const {
  language,
  getCurrentMessages,
  isOverview,
} = useMainState();
const router = useRouter();
const {
  getUser: getProductTourUser,
} = useProductTour();

const { userData } = useUserState();
const { init: initSatismeter } = useSatismeter({
  writeKey: import.meta.env.VUE_APP_SATISMETER_KEY,
  enabled: import.meta.env.PROD,
});

const lampoLocaleConfig = reactive({
  locale: language.value,
  messages: {} as Record<string, Record<string, string>>,
});

watch(language, async () => {
  lampoLocaleConfig.locale = language.value;
  lampoLocaleConfig.messages[language.value] = getCurrentMessages();
}, { immediate: true });

watch(isOverview, () => {
  if (isOverview.value
    && window.location.pathname.match(/\/property\/settings|preferences|setup/)) { // is not a valid section for overview
    router.push(getUserTypeDefaultRedirect());
  }
});

const beforeUnloadListener = async () => {
  const apcUser = await getProductTourUser();
  if (apcUser) {
    browserStorage.setItem('flow_sequence', Number(apcUser.flow_sequence));
  }
};

onMounted(async () => {
  window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });

  const intercomCredentials = await getUserIntercomCredentials() ?? {};

  const { initChatBot } = useChatBot({
    user_id: intercomCredentials.user_id,
    user_hash: intercomCredentials.intercom_secure_id,
    impersonated: auth.isImpersonatedUser(),
  });

  initChatBot();

  initSatismeter({
    id: `${userData.id}${auth.isImpersonatedUser() ? '-impersonated' : ''}`,
    email: userData.email,
    first_name: userData.firstName,
    last_name: userData.lastName,
  });

  try {
    const techTracking = await getTechTracking();
    if (techTracking) {
      techTracking.init();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error initializing tech tracking', error);
  }
});
</script>

<template>
  <LpSetup
    :config="lampoLocaleConfig"
  >
    <RouterView v-slot="{ Component }">
      <transition
        name="fade"
        mode="out-in"
      >
        <component :is="Component" />
      </transition>
    </RouterView>
  </LpSetup>
</template>
